import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState,
} from "react";
import { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { getAllBranch, getAllRoutes, getAllRoutesByCompany } from "../../services";
import { Branch } from "../../types/user.type";
import { SaleRoute } from "../../types/sale-route.type";
import { useUserRole } from "../../utils/hooks/useUserRole";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import Grid from "@mui/material/Grid";
import dayjs from "dayjs";
import "dayjs/locale/th";

dayjs.locale("th");

interface ReportFilterProps {
  selectedDate: Dayjs | null;
  setSelectedDate: Dispatch<SetStateAction<Dayjs | null>>;
  // uuid ของสาขา/สายส่ง ("all" = ทั้งหมด)
  selectedBranch: string;
  setSelectedBranch: Dispatch<SetStateAction<string>>;
  selectedRoute: string;
  setSelectedRoute: Dispatch<SetStateAction<string>>;
}

interface FilterFieldProps {
  label: string;
  children: React.ReactNode;
}

const FilterField = ({ label, children }: FilterFieldProps) => (
  <Grid size={{ xs: 12, sm: 4 }}>
    <Grid container alignItems="center" spacing={1}>
      <Grid size={3}>
        <Typography variant="body2" noWrap>
          {label}
        </Typography>
      </Grid>
      <Grid size={9}>{children}</Grid>
    </Grid>
  </Grid>
);

export default function DeductionFilter({
  selectedDate,
  setSelectedDate,
  selectedBranch,
  setSelectedBranch,
  selectedRoute,
  setSelectedRoute,
}: ReportFilterProps) {
  const { isHeadOfficeAccounting } = useUserRole();
  const [branches, setBranches] = useState<Branch[]>([]);
  const [routes, setRoutes] = useState<SaleRoute[]>([]);

  const onGetAllBranch = useCallback(async () => {
    const {
      data: { data },
    } = await getAllBranch();
    setBranches(data);
  }, []);

  // HO เลือกสาขาก่อน จึงดึงเฉพาะสายของสาขานั้น; branch user ถูก scope โดย API อยู่แล้ว
  const onGetAllRoutes = useCallback(async () => {
    if (isHeadOfficeAccounting) {
      if (selectedBranch === "all") {
        setRoutes([]);
        return;
      }
      // selectedBranch เป็น uuid (ใช้กับ /deductions-report) แต่ /routes กรองด้วย
      // company_id (ตัวเลขของสาขา) จึง map uuid -> company_id ก่อนเรียก
      const companyId = branches.find(
        (branch) => branch.uuid === selectedBranch,
      )?.company_id;
      if (!companyId) {
        setRoutes([]);
        return;
      }
      const {
        data: { data },
      } = await getAllRoutes(companyId);
      setRoutes(Array.isArray(data) ? data : data.rows ?? []);
    } else {
      const {
        data: { data },
      } = await getAllRoutesByCompany();
      setRoutes(data);
    }
  }, [isHeadOfficeAccounting, selectedBranch, branches]);

  useEffect(() => {
    // /branches เป็น HO-only; branch user ถูก scope โดย API อยู่แล้ว
    if (isHeadOfficeAccounting) {
      onGetAllBranch();
    }
  }, [isHeadOfficeAccounting, onGetAllBranch]);

  useEffect(() => {
    onGetAllRoutes();
  }, [onGetAllRoutes]);

  const routeOptions = routes;

  const isRouteDisabled = isHeadOfficeAccounting && selectedBranch === "all";

  const handleChangeBranch = (branchUuid: string) => {
    setSelectedBranch(branchUuid);
    // รีเซ็ตสายส่งเมื่อเปลี่ยนสาขา เพราะสายขึ้นกับสาขา
    setSelectedRoute("all");
  };

  return (
    <Grid container spacing={2} mb={2}>
      <FilterField label="เดือน-ปี">
        {/* year_month เป็น required ของ API — ไม่มีปุ่มล้างเดือน */}
        <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
          <DatePicker
            views={["year", "month"]}
            openTo="month"
            value={selectedDate}
            onChange={setSelectedDate}
            format="MMMM-YYYY"
            slotProps={{
              textField: { size: "small", fullWidth: true },
            }}
          />
        </LocalizationProvider>
      </FilterField>

      {isHeadOfficeAccounting && (
        <FilterField label="สาขา">
          <TextField
            select
            size="small"
            fullWidth
            value={selectedBranch}
            onChange={(e) => handleChangeBranch(e.target.value)}
          >
            <MenuItem value="all">ทั้งหมด</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.uuid} value={branch.uuid}>
                {branch.branch_code} - {branch.branch_name}
              </MenuItem>
            ))}
          </TextField>
        </FilterField>
      )}

      <FilterField label="สายส่ง">
        <TextField
          select
          size="small"
          fullWidth
          disabled={isRouteDisabled}
          value={selectedRoute}
          onChange={(e) => setSelectedRoute(e.target.value)}
          helperText={isRouteDisabled ? "เลือกสาขาก่อน" : undefined}
        >
          <MenuItem value="all">ทั้งหมด</MenuItem>
          {routeOptions.map((route) => (
            <MenuItem key={route.uuid} value={route.uuid}>
              {route.route_code} - {route.route_name}
            </MenuItem>
          ))}
        </TextField>
      </FilterField>
    </Grid>
  );
}
